/**
 * Service Area List Component
 *
 * Table displaying all service areas with search, filter, and CRUD actions.
 * Follows LocationsManagement.tsx pattern from locations feature.
 *
 * Features:
 * - Search by name
 * - Filter by franchise (All, Hamilton, North York)
 * - Day indicators (M T W T F S S)
 * - Edit and Archive/Activate actions
 * - Optimistic updates
 *
 * @example
 * <ServiceAreaList />
 */

import React, { useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Plus,
  Search,
  Edit,
  Archive,
  CheckCircle,
  MapPin,
  Building2,
} from 'lucide-react'
import { toast } from '@/components/ui/use-toast'
import { ServiceAreaEditor } from './ServiceAreaEditor'
import {
  useServiceAreas,
  useUpdateServiceArea,
  useDeleteServiceArea,
} from '../hooks/useServiceAreas'
import {
  formatDaysDisplay,
  getFranchiseLabel,
  getStatusLabel,
} from '../utils/schedulerFormatters'
import type { ServiceArea, FranchiseRegion } from '../types'

type FranchiseFilter = 'all' | 'hamilton' | 'northyork'

export function ServiceAreaList() {
  const [searchTerm, setSearchTerm] = useState('')
  const [franchiseFilter, setFranchiseFilter] =
    useState<FranchiseFilter>('all')
  const [editorOpen, setEditorOpen] = useState(false)
  const [selectedArea, setSelectedArea] = useState<ServiceArea | undefined>(
    undefined
  )

  const updateMutation = useUpdateServiceArea()
  const deleteMutation = useDeleteServiceArea()

  // Fetch service areas with filter
  const queryOptions =
    franchiseFilter === 'all'
      ? {}
      : { franchise_region: franchiseFilter as FranchiseRegion }

  const { data, isLoading, error } = useServiceAreas(queryOptions)
  const serviceAreas = data?.areas || []

  // Filter by search term
  const filteredAreas = serviceAreas.filter((area) =>
    area.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleNewArea = () => {
    setSelectedArea(undefined)
    setEditorOpen(true)
  }

  const handleEditArea = (area: ServiceArea) => {
    setSelectedArea(area)
    setEditorOpen(true)
  }

  const handleToggleActive = async (area: ServiceArea) => {
    const newStatus = !area.is_active

    try {
      await updateMutation.mutateAsync({
        id: area.id,
        data: { is_active: newStatus },
      })

      toast({
        title: newStatus ? 'Service Area Activated' : 'Service Area Archived',
        description: `${area.name} is now ${newStatus ? 'active' : 'archived'}.`,
      })
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to update service area status.',
        variant: 'destructive',
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
  const handleDelete = async (area: ServiceArea) => {
    if (
      !confirm(
        `Are you sure you want to delete "${area.name}"? This action cannot be undone.`
      )
    ) {
      return
    }

    try {
      await deleteMutation.mutateAsync(area.id)

      toast({
        title: 'Service Area Deleted',
        description: `${area.name} has been permanently deleted.`,
      })
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to delete service area.',
        variant: 'destructive',
      })
    }
  }

  const handleEditorSuccess = () => {
    setEditorOpen(false)
    setSelectedArea(undefined)
  }

  // Day indicators component
  const DayIndicators = ({ area }: { area: ServiceArea }) => {
    const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
    const dayKeys: Array<keyof ServiceArea> = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ]

    return (
      <div className="flex gap-1">
        {days.map((day, index) => {
          const isActive = area[dayKeys[index]] === true
          return (
            <div
              key={`${day}-${index}`}
              className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
              title={
                [
                  'Monday',
                  'Tuesday',
                  'Wednesday',
                  'Thursday',
                  'Friday',
                  'Saturday',
                  'Sunday',
                ][index]
              }
            >
              {day}
            </div>
          )
        })}
      </div>
    )
  }

  // Stats
  const stats = {
    total: serviceAreas.length,
    active: serviceAreas.filter((a) => a.is_active).length,
    hamilton: serviceAreas.filter((a) => a.franchise_region === 'hamilton')
      .length,
    northyork: serviceAreas.filter((a) => a.franchise_region === 'northyork')
      .length,
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-destructive mb-2">
            Error Loading Service Areas
          </h2>
          <p className="text-muted-foreground">
            Unable to load service areas. Please try again later.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Service Areas</h1>
          <p className="text-muted-foreground">
            Manage service area configurations and availability schedules.
          </p>
        </div>
        <Button onClick={handleNewArea}>
          <Plus className="mr-2 h-4 w-4" />
          Add Service Area
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Service Areas
            </CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Configured areas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
            <p className="text-xs text-muted-foreground">
              Available for scheduling
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hamilton</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.hamilton}</div>
            <p className="text-xs text-muted-foreground">HQ franchise areas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">North York</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.northyork}</div>
            <p className="text-xs text-muted-foreground">
              North York franchise areas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Service Area Directory</CardTitle>
          <CardDescription>
            View and manage service area configurations.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search service areas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={franchiseFilter}
                onChange={(e) =>
                  setFranchiseFilter(e.target.value as FranchiseFilter)
                }
                className="px-3 py-2 border border-input rounded-md bg-background text-sm"
              >
                <option value="all">All Franchises</option>
                <option value="hamilton">Hamilton</option>
                <option value="northyork">North York</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Areas Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Franchise</TableHead>
                <TableHead>Days Available</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="w-[150px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAreas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="text-muted-foreground">
                      {searchTerm
                        ? 'No service areas match your search.'
                        : 'No service areas configured yet.'}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAreas.map((area) => (
                  <TableRow key={area.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {area.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Order: {area.display_order}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getFranchiseLabel(area.franchise_region)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <DayIndicators area={area} />
                        <div className="text-xs text-muted-foreground">
                          {formatDaysDisplay(area)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={area.is_active ? 'default' : 'secondary'}
                      >
                        {getStatusLabel(area.is_active)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditArea(area)}
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-8 w-8 ${!area.is_active ? 'text-success' : 'text-warning'}`}
                          onClick={() => handleToggleActive(area)}
                          disabled={updateMutation.isPending}
                          title={
                            area.is_active
                              ? 'Archive service area'
                              : 'Activate service area'
                          }
                        >
                          {area.is_active ? (
                            <Archive className="h-4 w-4" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Service Area Editor Dialog */}
      <ServiceAreaEditor
        serviceArea={selectedArea}
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSuccess={handleEditorSuccess}
      />
    </div>
  )
}
